<!--
  ChatPanel.vue — the per-tracker chat window (go.lacak.io tracker chat).
  Opened from the object row's ⋮ → Chat action. Lists messages (tracker/chat/message/list)
  and sends new ones (tracker/chat/message/create). The real app POLLS chat (README: three
  timed loops), not the realtime socket; here we poll message/list every 3s.
  Reconstructed from the deployed chat UX; internal impl re-authored.
-->
<template>
  <div class="chat-panel">
    <div class="chat-head">
      <i class="material-icons">chat</i>
      <span class="chat-title">{{ tracker.label || ('#' + tracker.id) }}</span>
      <button class="chat-x" @click="$emit('close')"><i class="material-icons">close</i></button>
    </div>
    <div ref="log" class="chat-log">
      <div v-for="(m, i) in messages" :key="i" class="chat-msg" :class="[m.incoming ? 'in' : 'out', { failed: m.failed }]">
        <div class="chat-bubble">{{ m.text }}<span class="chat-time">{{ m.failed ? 'not sent' : m.time }}</span></div>
      </div>
      <div v-if="!messages.length && loadError" class="chat-empty">Couldn't load messages — retrying…</div>
      <div v-else-if="!messages.length" class="chat-empty">No messages yet.</div>
    </div>
    <div class="chat-input">
      <input v-model="draft" placeholder="Type a message…" @keyup.enter="send" />
      <button :disabled="!draft.trim() || sending" @click="send"><i class="material-icons">send</i></button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatPanel',
  props: { tracker: { type: Object, required: true } },
  data() { return { messages: [], draft: '', sending: false, timer: null, loadError: false, loadSeq: 0 } },
  watch: { 'tracker.id': { immediate: true, handler() { this.messages = []; this.loadError = false; this.load() } } },
  mounted() { this.timer = setInterval(this.load, 3000) },
  beforeDestroy() { if (this.timer) clearInterval(this.timer) },
  methods: {
    async load() {
      const seq = ++this.loadSeq
      try {
        const { request } = await import('@/api/request')
        const list = (await request('tracker/chat/message/list', { tracker_id: this.tracker.id }, 'list')) || []
        if (seq !== this.loadSeq) return // a newer load already applied — ignore stale result
        // preserve any un-persisted failed sends so the 3s poll doesn't erase them
        const failed = this.messages.filter((m) => m.failed)
        this.messages = list.concat(failed)
        this.loadError = false
        this.$nextTick(() => { const l = this.$refs.log; if (l) l.scrollTop = l.scrollHeight })
      } catch (e) {
        if (seq === this.loadSeq) this.loadError = true
      }
    },
    async send() {
      const text = this.draft.trim(); if (!text) return
      this.sending = true
      const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      const msg = { text, incoming: false, time }
      this.messages.push(msg) // optimistic
      this.draft = ''
      try {
        const { request } = await import('@/api/request')
        await request('tracker/chat/message/create', { tracker_id: this.tracker.id, text })
        await this.load() // reconcile with the server list (single source of truth, avoids dupes)
      } catch (e) { this.$set(msg, 'failed', true) } finally { this.sending = false }
      this.$nextTick(() => { const l = this.$refs.log; if (l) l.scrollTop = l.scrollHeight })
    }
  }
}
</script>

<style scoped>
.chat-panel { position: absolute; right: 12px; bottom: 12px; width: 320px; height: 420px; background: #fff; border-radius: 8px; box-shadow: 0 3px 16px rgba(0,0,0,.28); display: flex; flex-direction: column; z-index: 660; font-family: Roboto, sans-serif; }
.chat-head { display: flex; align-items: center; gap: 8px; padding: 12px 14px; border-bottom: 1px solid #eceff1; }
.chat-head .material-icons { color: #1e88c7; font-size: 20px; }
.chat-title { flex: 1; font-size: 15px; font-weight: 500; color: #263238; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-x { background: none; border: none; color: #90a4ae; cursor: pointer; display: flex; }
.chat-log { flex: 1; overflow-y: auto; padding: 12px; background: #f7f9fb; }
.chat-msg { display: flex; margin-bottom: 8px; }
.chat-msg.out { justify-content: flex-end; }
.chat-bubble { max-width: 78%; padding: 7px 10px; border-radius: 10px; font-size: 13px; line-height: 1.4; color: #263238; }
.chat-msg.in .chat-bubble { background: #fff; border: 1px solid #e3e8ec; }
.chat-msg.out .chat-bubble { background: #d7ecfb; }
.chat-msg.failed .chat-bubble { background: #fdecea; border: 1px solid #f5c6cb; }
.chat-msg.failed .chat-time { color: #e53935; }
.chat-time { display: block; font-size: 10px; color: #90a4ae; margin-top: 3px; text-align: right; }
.chat-empty { text-align: center; color: #b0bec5; font-size: 13px; margin-top: 20px; }
.chat-input { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-top: 1px solid #eceff1; }
.chat-input input { flex: 1; border: 1px solid #cfd8dc; border-radius: 18px; padding: 8px 12px; font-size: 13px; outline: none; }
.chat-input button { background: #1e88c7; border: none; color: #fff; width: 34px; height: 34px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.chat-input button:disabled { background: #b0bec5; cursor: default; }
.chat-input .material-icons { font-size: 18px; }
</style>
